import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { buildClassName } from 'utils/build-class-name';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_CALC_EMAIL_CLICK,
  GTM_CALC_PDF_CLICK,
  GTM_CALC_LEAD_QUAL_SUB,
} from 'constants/events';
import styles from './SaveShare.module.scss';

interface SaveShareProps {
  mortgageData: {
    homePrice: number;
    downPayment: number;
    interestRate: number;
    amortization: number;
    monthlyPayment: number;
    totalInterest: number;
  };
}

const SaveShare = ({ mortgageData }: SaveShareProps) => {
  const [email, setEmail] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const generateShareUrl = () => {
    const params = new URLSearchParams({
      hp: mortgageData.homePrice.toString(),
      dp: mortgageData.downPayment.toString(),
      ir: mortgageData.interestRate.toString(),
      am: mortgageData.amortization.toString(),
    });
    return typeof window !== 'undefined' ? `${window.location.origin}?${params.toString()}` : '';
  };

  const handleCopyLink = () => {
    const url = generateShareUrl();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  const handleEmailResults = async () => {
    if (!email) {
      return;
    }
    AnalyticsController.trackEvent(GTM_CALC_LEAD_QUAL_SUB, 'Mortgage Calculator', 'email');
    setEmail('');
    setIsOpen(false);
  };

  const handleDownloadPDF = () => {
    AnalyticsController.trackEvent(GTM_CALC_PDF_CLICK, 'Mortgage Calculator');

    const doc = new jsPDF();
    const { homePrice, downPayment, interestRate, amortization, monthlyPayment, totalInterest } = mortgageData;

    const downPaymentPercent = ((downPayment / homePrice) * 100).toFixed(1);
    const principalAmount = homePrice - downPayment;
    const totalPayment = monthlyPayment * amortization * 12;
    const fmt = (n: number) => `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    // Header
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Mortgage Summary', 105, 25, { align: 'center' });

    // Date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated on ${new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`, 105, 33, { align: 'center' });

    // Divider
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 38, 190, 38);

    // Monthly Payment highlight
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Monthly Payment', 105, 50, { align: 'center' });
    doc.setFontSize(28);
    doc.text(fmt(monthlyPayment), 105, 62, { align: 'center' });

    // Mortgage Details section
    let y = 80;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Mortgage Details', 20, y);
    y += 4;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, 190, y);
    y += 10;

    doc.setFontSize(11);
    const details: [string, string][] = [
      ['Home Price', fmt(homePrice)],
      ['Down Payment', `${fmt(downPayment)} (${downPaymentPercent}%)`],
      ['Mortgage Amount', fmt(principalAmount)],
      ['Interest Rate', `${interestRate}%`],
      ['Amortization Period', `${amortization} years`],
    ];

    for (const [label, value] of details) {
      doc.setFont('helvetica', 'normal');
      doc.text(label, 25, y);
      doc.setFont('helvetica', 'bold');
      doc.text(value, 185, y, { align: 'right' });
      y += 9;
    }

    // Cost Summary section
    y += 6;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Cost Summary', 20, y);
    y += 4;
    doc.line(20, y, 190, y);
    y += 10;

    doc.setFontSize(11);
    const costs: [string, string][] = [
      ['Total Interest Paid', fmt(totalInterest)],
      ['Total Amount Paid', fmt(totalPayment)],
    ];

    for (const [label, value] of costs) {
      doc.setFont('helvetica', 'normal');
      doc.text(label, 25, y);
      doc.setFont('helvetica', 'bold');
      doc.text(value, 185, y, { align: 'right' });
      y += 9;
    }

    // Footer
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 270, 190, 270);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text(
      'This calculator is for informational purposes only. Please consult with a mortgage professional for personalized advice.',
      105, 277,
      { align: 'center', maxWidth: 170 }
    );

    doc.save('mortgage-summary.pdf');
  };

  const handleShareSocial = (platform: string) => {
    AnalyticsController.trackEvent(GTM_CALC_LEAD_QUAL_SUB, 'Mortgage Calculator', `share_${platform}`);
    const url = generateShareUrl();
    const text = `Check out my mortgage calculations: Monthly payment $${mortgageData.monthlyPayment.toLocaleString()}`;

    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
    }

    if (shareUrl && typeof window !== 'undefined') {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className={styles.component}>
      <Dialog open={isOpen} onOpenChange={open => {
        if (open) {
          AnalyticsController.trackEvent(GTM_CALC_EMAIL_CLICK, 'Mortgage Calculator');
        }
        setIsOpen(open);
      }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className={styles.button}>
            <span>📧</span> Email Results
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email Your Results</DialogTitle>
            <DialogDescription>
              Enter your email address to receive a detailed summary of your mortgage calculations.
            </DialogDescription>
          </DialogHeader>
          <div className={styles.dialogContent}>
            <div className={styles.inputGroup}>
              <Label htmlFor="email" className={styles.inputLabel}>Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={styles.input}
              />
            </div>
            <div className={styles.resultsPreview}>
              <p className={styles.resultsPreviewTitle}>Your results include:</p>
              <ul className={styles.resultsPreviewList}>
                <li>Monthly payment: ${mortgageData.monthlyPayment.toLocaleString()}</li>
                <li>Total interest: ${mortgageData.totalInterest.toLocaleString()}</li>
                <li>Amortization schedule</li>
                <li>Money-saving tips</li>
              </ul>
            </div>
            <Button onClick={handleEmailResults} className={styles.submitButton}>
              Send Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Button variant="outline" size="sm" className={styles.button} onClick={handleCopyLink}>
        <span>📋</span> Copy Link
      </Button>

      <Button variant="outline" size="sm" className={styles.button} onClick={handleDownloadPDF}>
        <span>📥</span> Download PDF
      </Button>

      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className={styles.button}>
            <span>🔗</span> Share
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Your Results</DialogTitle>
            <DialogDescription>
              Share your mortgage calculations with friends and family
            </DialogDescription>
          </DialogHeader>
          <div className={styles.shareButtons}>
            <Button
              variant="outline"
              className={styles.shareButton}
              onClick={() => handleShareSocial('twitter')}
            >
              <div className={buildClassName(styles.socialIcon, styles.twitterIcon)}>
                𝕏
              </div>
              Share on Twitter
            </Button>
            <Button
              variant="outline"
              className={styles.shareButton}
              onClick={() => handleShareSocial('facebook')}
            >
              <div className={buildClassName(styles.socialIcon, styles.facebookIcon)}>
                f
              </div>
              Share on Facebook
            </Button>
            <Button
              variant="outline"
              className={styles.shareButton}
              onClick={() => handleShareSocial('linkedin')}
            >
              <div className={buildClassName(styles.socialIcon, styles.linkedinIcon)}>
                in
              </div>
              Share on LinkedIn
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SaveShare;
